import { t } from "i18next";
import { Eye } from "lucide-react";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { AmountMinor } from "@/features/shared";
import type { TransactionDto } from "@/features/wallet/api/dto";
import { TransactionStatusBadge } from "./transaction-status-badge";

/**
 * Colonnes de l'historique des transactions (`POST /Transactions/all`) —
 * écran d'orchestration back-office. Montants formatés via `AmountMinor`
 * (helper `*Minor`, guide §4.3) ; identifiants et références en
 * `NoTranslate` (règle notranslate). Les filtres API (`TransactionFilter`)
 * sont pilotés par `TransactionsFilters` (barre externe) : les champs de
 * recherche DataTableClar sont masqués (`denied`) pour ne pas simuler un
 * filtre non câblé en pagination serveur.
 */
export const transactionsColumns: ColumnsClarFactory<TransactionDto> = (navigate) => [
  {
    id: "reference",
    accessorKey: "reference",
    header: t("wallet.transactions.columns.reference"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "type",
    accessorKey: "type",
    header: t("wallet.transactions.columns.type"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {getValue() as string}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "sourceWalletId",
    accessorKey: "sourceWalletId",
    header: t("wallet.transactions.columns.sourceWalletId"),
    cell: ({ getValue }) => {
      const id = getValue() as string;
      return (
        <NoTranslate as="span" className="font-mono text-xs">
          {id ? `${id.slice(0, 8)}…` : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "destination",
    header: t("wallet.transactions.columns.destination"),
    cell: ({ row }) => {
      const destination =
        row.original.destinationWalletId ?? row.original.destinationExternalRef;
      return (
        <NoTranslate as="span" className="font-mono text-xs">
          {destination
            ? destination.length > 12
              ? `${destination.slice(0, 8)}…`
              : destination
            : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "amount",
    accessorKey: "amountMinor",
    header: t("wallet.transactions.columns.amount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.amountMinor}
        currency={row.original.currency}
        className="font-medium"
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "fee",
    accessorKey: "feeAmountMinor",
    header: t("wallet.transactions.columns.fee"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.feeAmountMinor}
        currency={row.original.currency}
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("wallet.transactions.columns.status"),
    cell: ({ row }) => <TransactionStatusBadge status={row.original.status} />,
    meta: { type: "denied" },
  },
  {
    id: "initiatedAt",
    accessorKey: "initiatedAt",
    header: t("wallet.transactions.columns.initiatedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <RowActionsMenu
          actions={[
            {
              id: "view",
              label: t("wallet.transactions.actions.view"),
              icon: Eye,
              onClick: () =>
                navigate(`/app/wallet/transactions/${row.original.id}`),
            },
          ]}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
